// Preparing clipboard HTML for the slide editors.
//
// Two jobs, both about pasting from a word processor.
//
// The first is fidelity. Word puts a paragraph's *base* formatting — the font,
// its size, the colour — in a <style> block keyed to `class=MsoNormal`, and
// writes only the deviations inline. The slide sanitizer drops <style>, because
// a stylesheet from a shared deck is not something to run in our own document.
// The consequence is that uniformly formatted Word text arrives carrying no
// formatting whatsoever: the deviations survive and the baseline they deviate
// from does not. Resolving those rules onto the elements first is what makes
// "keep the source formatting" mean anything.
//
// The second is the plain-text alternative, so an author can say "just the
// words" and get the slide's own typography instead.

/**
 * CSS properties worth carrying across from a pasted document.
 *
 * An allowlist rather than everything, because a word processor's stylesheet
 * is full of page-layout rules — margins in inches, mso-* private properties,
 * fixed widths — that describe an A4 page and mean nothing on a 16:9 slide.
 * Applying those wholesale is how a paste ends up with a paragraph indented
 * off the edge of the canvas.
 */
const CARRIED_PROPERTIES = new Set([
    // Character
    'font-family',
    'font-size',
    'font-weight',
    'font-style',
    'font-variant',
    'text-decoration',
    'text-decoration-line',
    'text-transform',
    'letter-spacing',
    'color',
    'background-color',
    'vertical-align',
    // Paragraph. The app's own stylesheet sets `p { margin-bottom: 1em;
    // line-height: 1.6 }`, so without carrying these a pasted document loses
    // its spacing and indents entirely and every paragraph comes out on the
    // slide's rhythm rather than its own.
    'text-align',
    'line-height',
    'text-indent',
    'margin',
    'margin-top',
    'margin-bottom',
    'margin-left',
    'margin-right',
    'padding-left'
]);

/**
 * The character-level properties "merge formatting" gives up.
 *
 * Word's middle option keeps the emphasis and the structure — bold, italic,
 * lists, alignment — and adopts the destination's typeface, size and colour.
 * That is the useful one when pasting a paragraph out of a document into a
 * slide whose look you have already settled.
 */
const MERGED_AWAY = new Set([
    'font-family',
    'font-size',
    'color',
    'background-color',
    'letter-spacing',
    'line-height',
    'text-transform',
    'font-variant'
]);

/** Split a declaration list on semicolons that are not inside quotes. */
const splitDeclarations = (text) => {
    const parts = [];
    let current = '';
    let quote = null;
    for (const char of String(text || '')) {
        if (quote) {
            if (char === quote) quote = null;
            current += char;
            continue;
        }
        if (char === '"' || char === "'") { quote = char; current += char; continue; }
        if (char === ';') { parts.push(current); current = ''; continue; }
        current += char;
    }
    parts.push(current);
    return parts.map((part) => part.trim()).filter(Boolean);
};

/** `font-size:12pt; mso-bidi-x:1` -> [['font-size','12pt']], allowlisted. */
const usefulDeclarations = (text) => splitDeclarations(text).reduce((acc, part) => {
    const colon = part.indexOf(':');
    if (colon === -1) return acc;
    const property = part.slice(0, colon).trim().toLowerCase();
    const value = part.slice(colon + 1).trim();
    if (!value || !CARRIED_PROPERTIES.has(property)) return acc;
    // mso-* values are Word's private vocabulary and mean nothing to a browser.
    if (/^mso-/i.test(value)) return acc;
    acc.push([property, value]);
    return acc;
}, []);

/**
 * The rules in a stylesheet, as [selector, declarations] pairs.
 *
 * A deliberately small parser: word-processor stylesheets are flat lists of
 * simple rules, and the alternative — shipping a real CSS parser to read a
 * paste — is not worth the bytes. At-rules are skipped rather than descended
 * into, because @media and @page are exactly the page-layout instructions the
 * allowlist above is already refusing.
 */
export const parseStyleRules = (css) => {
    const cleaned = String(css || '')
        .replace(/<!--/g, ' ')
        .replace(/-->/g, ' ')
        .replace(/\/\*[\s\S]*?\*\//g, ' ');

    const rules = [];
    const pattern = /([^{}]+)\{([^{}]*)\}/g;
    let match = pattern.exec(cleaned);
    while (match) {
        const selectorText = match[1].trim();
        const declarations = usefulDeclarations(match[2]);
        if (selectorText && !selectorText.startsWith('@') && declarations.length) {
            selectorText.split(',').forEach((selector) => {
                const trimmed = selector.trim();
                if (trimmed) rules.push({ selector: trimmed, declarations });
            });
        }
        match = pattern.exec(cleaned);
    }
    return rules;
};

/**
 * Resolve a document's <style> rules onto the elements they match, as inline
 * styles, then drop the stylesheet.
 *
 * Anything already written inline is kept and wins: those are the author's
 * deviations from the base, and the whole point of the exercise is that both
 * survive. The parsing happens in a detached document — nothing is attached,
 * no resource loads — and the result still goes through the sanitizer
 * afterwards, so this widens what an author's formatting can express without
 * widening what a paste is allowed to do.
 */
export const inlineStyleBlocks = (html) => {
    const source = String(html || '');
    if (!source || typeof DOMParser === 'undefined') return source;
    if (!/<style/i.test(source)) return source;

    let doc;
    try {
        doc = new DOMParser().parseFromString(source, 'text/html');
    } catch {
        return source;
    }
    if (!doc?.body) return source;

    const sheets = Array.from(doc.querySelectorAll('style'));
    if (!sheets.length) return source;

    const rules = sheets.flatMap((sheet) => parseStyleRules(sheet.textContent));
    rules.forEach(({ selector, declarations }) => {
        let matches;
        try {
            matches = doc.body.querySelectorAll(selector);
        } catch {
            return;   // a selector this browser cannot parse — skip that rule
        }
        matches.forEach((node) => {
            const existing = node.getAttribute('style') || '';
            // Rule declarations first so an inline one later in the string
            // overrides it, which is how the cascade already reads.
            const inherited = declarations.map(([property, value]) => `${property}: ${value}`).join('; ');
            const combined = existing ? `${inherited}; ${existing}` : inherited;
            node.setAttribute('style', combined);
        });
    });

    sheets.forEach((sheet) => sheet.remove());
    return doc.body.innerHTML;
};

/**
 * Keep the emphasis and the structure, drop the typeface.
 *
 * The middle of Word's three choices: bold, italic, underline, lists,
 * alignment and paragraph spacing all survive; font, size and colour are
 * given up so the pasted text takes on the slide's own look. Word's own
 * `class` attributes go too — they carry no styling once the stylesheet has
 * been resolved, and leaving them behind only invites a later stylesheet to
 * pick them up again.
 */
export const mergeFormatting = (html) => {
    const source = String(html || '');
    if (!source || typeof DOMParser === 'undefined') return source;

    let doc;
    try {
        doc = new DOMParser().parseFromString(source, 'text/html');
    } catch {
        return source;
    }
    if (!doc?.body) return source;

    doc.body.querySelectorAll('[style], [class]').forEach((node) => {
        node.removeAttribute('class');
        const declarations = splitDeclarations(node.getAttribute('style'))
            .filter((part) => {
                const colon = part.indexOf(':');
                if (colon === -1) return false;
                return !MERGED_AWAY.has(part.slice(0, colon).trim().toLowerCase());
            });
        if (declarations.length) node.setAttribute('style', declarations.join('; '));
        else node.removeAttribute('style');
    });

    return doc.body.innerHTML;
};

/**
 * The words, with the paragraph breaks kept and everything else dropped.
 *
 * Used for "keep text only", and as the fallback when the clipboard offers no
 * plain-text flavour of its own. Block elements become newlines so a pasted
 * list does not collapse into one run-on line.
 */
export const htmlToPlainText = (html) => {
    const source = String(html || '');
    if (!source) return '';
    if (typeof DOMParser === 'undefined') return source.replace(/<[^>]*>/g, '');

    let doc;
    try {
        doc = new DOMParser().parseFromString(source, 'text/html');
    } catch {
        return source.replace(/<[^>]*>/g, '');
    }
    if (!doc?.body) return '';

    doc.body.querySelectorAll('style, script, head').forEach((node) => node.remove());
    doc.body.querySelectorAll('br').forEach((node) => node.replaceWith('\n'));
    doc.body.querySelectorAll('p, div, li, tr, h1, h2, h3, h4, h5, h6, blockquote')
        .forEach((node) => node.append('\n'));

    return (doc.body.textContent || '')
        .replace(/ /g, ' ')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
};

/** Plain text as HTML paragraphs, so "text only" still reads as paragraphs. */
export const plainTextToHtml = (text) => String(text || '')
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .map((paragraph) => `<p>${paragraph
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>')}</p>`)
    .join('');
